// The Variant group tab: which group of separate products this product
// belongs to (its colors, each a product of its own), what it is in that
// group, and the group's other products.
//
// The product's own group and value join the dialog's Save (register); adding
// or removing another product is written at once — it is that product's
// record, not this one's.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import ColorPickerEditor from "aldoo-ra/CMS/field-editors/color-picker"
import ImageEditor from "aldoo-ra/CMS/field-editors/image"
import CMSController from "aldoo-ra/CMS/controllers/cms-controller"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import { url } from "aldoo-ra/CMS/url"
import { ArrowTopRightOnSquareIcon, PencilSquareIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { useUrlParam } from "aldoo-ra/URLState"
import { storeProductUrl } from "aldoo-ra/Store/product-url"
import ProductPicker from "./ProductPicker"
import { addMembers, createGroup, listGroups, listMembers, loadProductGroup, removeMember, setProductGroup } from "./api"
import { DISPLAYS, keyFromLabel, textOf, withLabel } from "./values"

const notify = (message, isError) =>
  showNotification({ message, className: isError ? "bg-admin_error text-white" : "bg-admin_success text-white" })

const IMAGE_FIELD = { id: "image", label: "Swatch picture", type: "Image", multiple: false, formats: ["png", "jpg", "jpeg", "webp"] }
const COLOR_FIELD = { id: "hex", label: "Swatch color" }
const EMPTY_VALUE = { key: "", label: {}, hex: "", image: "" }
const ERRORS = { invalid_gid: "The group id may hold letters, digits, dots, dashes and underscores", gid_taken: "A group with this id exists already" }

const languageCode = (language) => (typeof language === "string" ? language : language?.code || language?.value || "en")
const imageEntry = (image) => (image && typeof image === "object" ? image : image ? { url: image } : null)

function Card({ title, description, actions, children }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-admin_bordered p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Typography component="h4" variant="admin_h4" className="my-0">{title}</Typography>
          {description && <p className="text-sm text-admin_text_secondary my-0 mt-1">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </div>
  )
}

function Field({ label, hint, className = "", children }) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="text-sm font-medium text-admin_text">{label}</span>
      {children}
      {hint && <span className="text-xs text-admin_text_secondary">{hint}</span>}
    </label>
  )
}

/** The little picture of a member: its value's swatch as the group shows it, or its thumbnail. */
function Swatch({ member, display }) {
  const value = member.value || {}
  if (display === "color" && value.hex) return <span className="w-10 h-10 rounded-full border border-admin_bordered shrink-0" style={{ background: value.hex }} />
  const picture = (display === "image" && value.image) || member.thumbnail
  if (picture) return <img src={url(picture)} alt="" className="w-10 h-10 rounded object-cover border border-admin_bordered shrink-0" />
  return <span className="w-10 h-10 rounded bg-admin_neutral_1 border border-admin_bordered shrink-0" />
}

function NewGroupForm({ onCreated, onCancel }) {
  const [draft, setDraft] = useState({ gid: "", label: "", axis: "color", display: "image" })
  const [busy, setBusy] = useState(false)
  const set = (patch) => setDraft((current) => ({ ...current, ...patch }))

  const create = async () => {
    if (!draft.gid.trim()) return notify("The group needs an id", true)
    setBusy(true)
    try {
      const res = await createGroup({ ...draft, gid: draft.gid.trim() })
      notify(`Group ${res.group?.gid || draft.gid} created`)
      onCreated(res.group)
    } catch (error) {
      notify(ERRORS[error.message] || error.message || "Failed to create the group", true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-md bg-admin_neutral_1/50 p-3" data-testid="variant-group-new">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Label" hint="The switcher's title on the product page">
          <Input variant="admin" value={draft.label} placeholder="Colors" onChange={(e) => set({ label: e.target.value })} data-testid="variant-group-new-label" />
        </Field>
        <Field label="Group id" hint="What the products name the group by: ts091-colors">
          <Input variant="admin" value={draft.gid} placeholder="ts091-colors" onChange={(e) => set({ gid: e.target.value })} data-testid="variant-group-new-gid" />
        </Field>
        <Field label="Axis" hint="What the products differ in">
          <Input variant="admin" value={draft.axis} placeholder="color" onChange={(e) => set({ axis: e.target.value })} />
        </Field>
        <Field label="Shown as">
          <DropdownSelect
            variant="admin"
            options={DISPLAYS}
            value={DISPLAYS.filter((option) => option.value === draft.display)}
            onChange={(picked) => set({ display: (Array.isArray(picked) ? picked[0] : picked)?.value || "image" })}
            enableResetButton={false}
            showCheckbox={false}
            className="w-full"
          />
        </Field>
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="admin_secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="admin_primary" onClick={create} disabled={busy} data-testid="variant-group-create">Create group</Button>
      </div>
    </div>
  )
}

export default function VariantGroupTab({ dataItem, product, language, register }) {
  const pid = (dataItem || product)?.pid
  // an imported product's group is the import's: it writes the group, the product's value in
  // it and so the members on every import — shown here, not edited (it would be undone)
  const importer = (dataItem || product)?.source?.importer || null
  const lang = languageCode(language)
  const cms = CMSController()

  const [loading, setLoading] = useState(true)
  const [groups, setGroups] = useState([])
  const [members, setMembers] = useState([])
  // what is stored, and what the tab now says
  const [saved, setSaved] = useState({ gid: "", value: EMPTY_VALUE })
  const [draft, setDraft] = useState({ gid: "", value: EMPTY_VALUE })
  const [creating, setCreating] = useState(false)
  const [picking, setPicking] = useState(false)

  // the product's group, once per product: a language switch (to type the
  // label in another language) keeps what the tab says
  const load = useCallback(async () => {
    if (!pid) return setLoading(false)
    try {
      const own = await loadProductGroup(pid)
      const current = { gid: own.variantGroup?.gid || "", value: { ...EMPTY_VALUE, ...(own.variantGroup?.value || {}) } }
      setSaved(current)
      setDraft(current)
      setMembers(own.members || [])
    } catch (error) {
      notify(error.message || "Failed to load the product's group", true)
    } finally {
      setLoading(false)
    }
  }, [pid])

  useEffect(() => {
    load()
  }, [load])

  // the groups, titled in the dialog's language
  useEffect(() => {
    let alive = true
    listGroups(lang)
      .then((res) => alive && setGroups(res.groups || []))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [lang])

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved)

  // the dialog's Save writes the product's group and value
  const save = async () => {
    const value = { ...draft.value }
    const image = imageEntry(value.image)
    if (image?.file) {
      const path = await cms.uploadFile({ file: image.file, category: "images" })
      if (!path) return false
      value.image = path
    } else {
      value.image = image?.url || ""
    }
    if (draft.gid && !value.key) value.key = keyFromLabel(textOf(value.label, lang))
    try {
      const res = await setProductGroup(pid, draft.gid, draft.gid ? value : undefined)
      const next = { gid: res.variantGroup?.gid || "", value: { ...EMPTY_VALUE, ...(res.variantGroup?.value || {}) } }
      setSaved(next)
      setDraft(next)
      setMembers(res.members || [])
      // written by the action: the form has nothing of it to write
      return true
    } catch (error) {
      notify(error.message || "Failed to save the variant group", true)
      return false
    }
  }

  const state = useRef({})
  state.current = { dirty, save }
  const registerRef = useRef(register)
  registerRef.current = register
  useEffect(() => {
    if (!registerRef.current) return
    registerRef.current({
      get dirty() {
        return state.current.dirty
      },
      save: () => state.current.save(),
    })
    return () => registerRef.current?.(null)
  }, [])

  const group = useMemo(() => groups.find((entry) => entry.gid === draft.gid) || null, [groups, draft.gid])
  // a group reads by what is in it: its products' shared name, their colors, how many — the id
  // underneath (and in the search, with the rest)
  const options = useMemo(
    () =>
      groups.map((entry) => {
        const title = entry.preview?.title || entry.label || entry.gid
        const values = (entry.preview?.values || []).map((value) => value.label).filter(Boolean)
        return { value: entry.gid, name: `${title}${values.length ? ` — ${values.join(", ")}` : ""} (${entry.members})`, group: entry, title, values }
      }),
    [groups]
  )
  const renderGroupOption = (option) => (
    <div className="flex items-center gap-3 min-w-0 py-0.5" data-testid="variant-group-option">
      <div className="flex -space-x-1 shrink-0">
        {(option.group?.preview?.values || []).slice(0, 5).map((value, index) =>
          value.image ? (
            <img key={index} src={value.image} alt="" className="w-5 h-5 rounded-full border border-admin_bordered ring-2 ring-admin_paper object-cover" />
          ) : (
            <span key={index} className="w-5 h-5 rounded-full border border-admin_bordered ring-2 ring-admin_paper bg-admin_neutral_1" style={value.hex ? { background: value.hex } : undefined} title={value.label} />
          )
        )}
      </div>
      <div className="flex flex-col min-w-0">
        <span className="truncate text-admin_text">
          {option.title} <span className="text-admin_text_secondary">({option.group?.members || 0})</span>
        </span>
        <span className="truncate text-xs text-admin_text_secondary">
          {option.values.join(" · ")}
          {option.values.length ? " · " : ""}
          <span className="font-mono">{option.value}</span>
        </span>
      </div>
    </div>
  )
  const setValue = (patch) => setDraft((current) => ({ ...current, value: { ...current.value, ...patch } }))
  const pickGroup = (gid) => setDraft((current) => ({ ...current, gid: gid || "" }))
  // a group picked but not saved yet: its products as they stand (this one joins on Save)
  const inSavedGroup = !!saved.gid && saved.gid === draft.gid
  useEffect(() => {
    if (!draft.gid || loading) return
    let alive = true
    listMembers(draft.gid)
      .then((res) => alive && setMembers(res.members || []))
      .catch(() => alive && setMembers([]))
    return () => {
      alive = false
    }
  }, [draft.gid, loading])

  const onCreated = async (created) => {
    setCreating(false)
    const all = await listGroups(lang).catch(() => null)
    if (all?.groups) setGroups(all.groups)
    if (created?.gid) pickGroup(created.gid)
  }

  const add = async (pids) => {
    if (!pids.length || !draft.gid) return
    try {
      const res = await addMembers(draft.gid, pids)
      setMembers(res.members || [])
      notify(pids.length > 1 ? `${pids.length} products added to the group` : "Product added to the group")
    } catch (error) {
      notify(error.message || "Failed to add the products", true)
    }
  }

  const remove = async (member) => {
    const ok = await showPromptDialogAsync({
      variant: "admin",
      title: "Remove from the group",
      message: `${textOf(member.name, lang) || member.pid} leaves the group and stops showing it as a sibling. The product itself stays as it is.`,
      yesLabel: "Remove",
    })
    if (!ok) return
    try {
      await removeMember(member.pid)
      setMembers((list) => list.filter((entry) => entry.pid !== member.pid))
      notify("Product removed from the group")
    } catch (error) {
      notify(error.message || "Failed to remove the product", true)
    }
  }

  // the member's own dialog, in a tab of its own (the products page opens ?pid=)
  // another product of the group, here in the dialog (the address names it: the products page opens it)
  const [, setOpenPid] = useUrlParam("pid", { default: "" })
  const open = (member) => setOpenPid(member.pid)

  if (!pid) {
    return <p className="text-sm text-admin_text_secondary">Save the product first: a group links products that exist.</p>
  }
  if (loading) return <p className="text-sm text-admin_text_secondary">Loading the variant group…</p>

  const display = group?.display || "image"
  const labelText = typeof draft.value.label === "string" ? draft.value.label : draft.value.label?.[lang] || ""

  return (
    <div className="flex flex-col gap-4" data-testid="variant-group-tab">
      <Card
        title="Group"
        description="Separate products shown as variants of each other — the colors of a model, each a product with its own stock and page. Every product of the group links to the others."
        actions={!creating && !importer && <Button variant="admin_secondary" onClick={() => setCreating(true)} data-testid="variant-group-new-open">New group</Button>}
      >
        {creating && <NewGroupForm onCreated={onCreated} onCancel={() => setCreating(false)} />}
        {importer ? (
          <div className="flex flex-col gap-1" data-testid="variant-group-locked">
            <span className="text-sm font-medium text-admin_text">This product's group</span>
            {draft.gid ? (
              <div className="rounded-lg border border-admin_bordered px-3 py-2 md:w-1/2">
                {renderGroupOption(options.find((option) => option.value === draft.gid) || { value: draft.gid, title: draft.gid, values: [], group: null })}
              </div>
            ) : (
              <span className="text-sm text-admin_text_secondary">No group</span>
            )}
            <span className="text-xs text-admin_text_secondary">From the import ({importer}): the group, this product's color in it and its products follow each import.</span>
          </div>
        ) : (
        <div className="flex flex-col md:flex-row gap-3 md:items-end">
          <Field label="This product's group" className="md:w-1/2">
            <DropdownSelect
              variant="admin"
              options={options}
              value={options.filter((option) => option.value === draft.gid)}
              onChange={(picked) => pickGroup((Array.isArray(picked) ? picked[0] : picked)?.value)}
              renderOption={renderGroupOption}
              placeholder="No group"
              searchEnabled
              showCheckbox={false}
              className="w-full"
              data-testid="variant-group-select"
            />
          </Field>
          {draft.gid && (
            <Button variant="admin_text" onClick={() => pickGroup("")} data-testid="variant-group-leave">Leave the group</Button>
          )}
        </div>
        )}
        {group && (
          <p className="text-xs text-admin_text_secondary my-0">
            Axis: {group.axis} · shown as {DISPLAYS.find((option) => option.value === group.display)?.name.toLowerCase()} · titled “{group.label || group.gid}”. The group's label, axis and display are edited in CMS → Product Group.
          </p>
        )}
        {dirty && <p className="text-xs text-admin_text_warning my-0">Saved with the product.</p>}
      </Card>

      {/* an imported product's own value is the import's: the members list below shows it */}
      {draft.gid && !importer && (
        <Card title="This product in the group" description={`What the switcher shows for this product. The group shows ${display === "color" ? "the color swatch" : display === "label" ? "the label" : "the picture (the product's own when none is set here)"}.`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label={`Label (${lang})`} hint="Black, Dark blue">
              <Input variant="admin" value={labelText} onChange={(e) => setValue({ label: withLabel(draft.value.label, lang, e.target.value) })} data-testid="variant-group-value-label" />
            </Field>
            <Field label="Key" hint="Stays the same in every language; made from the label when empty">
              <Input variant="admin" value={draft.value.key || ""} placeholder={keyFromLabel(labelText)} onChange={(e) => setValue({ key: e.target.value })} data-testid="variant-group-value-key" />
            </Field>
            <Field label="Position" hint="Lower first; empty sorts by label">
              <Input variant="admin" type="number" value={draft.value.order ?? ""} onChange={(e) => setValue({ order: e.target.value === "" ? undefined : Number(e.target.value) })} />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Swatch color" hint={display === "color" ? "Shown as the swatch" : "Used if the group is shown as color swatches"}>
              <ColorPickerEditor field={COLOR_FIELD} value={draft.value.hex || ""} onChange={(hex) => setValue({ hex: hex || "" })} />
            </Field>
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-admin_text">Swatch picture</span>
              <ImageEditor field={IMAGE_FIELD} showInfo={false} value={imageEntry(draft.value.image)} onChange={(image) => setValue({ image: image || "" })} />
              <span className="text-xs text-admin_text_secondary">Optional: the product's own picture is shown when none is set.</span>
            </div>
          </div>
        </Card>
      )}

      {draft.gid && (
        <Card
          title={`Products of the group (${members.length})`}
          description={inSavedGroup ? "The storefront shows the enabled ones that are listed; one out of stock is shown, marked." : "Save to put this product in the group."}
          actions={
            !importer && (
              <Button variant="admin_secondary" onClick={() => setPicking(true)} data-testid="variant-group-add">
                <PlusIcon className="w-4 h-4 mr-1 inline" />Add products
              </Button>
            )
          }
        >
          {!members.length && <p className="text-sm text-admin_text_secondary my-0">No other product is in this group yet.</p>}
          <div className="flex flex-col divide-y divide-admin_bordered">
            {members.map((member) => {
              const self = member.pid === pid
              return (
                <div key={member.pid} className="flex items-center gap-3 py-2" data-testid="variant-group-member">
                  <Swatch member={self ? { ...member, value: draft.value } : member} display={display} />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="text-sm font-medium text-admin_text truncate">
                      {textOf(member.name, lang) || member.pid}
                      {self && <span className="ml-2 text-xs text-admin_primary">this product</span>}
                    </span>
                    <span className="text-xs text-admin_text_secondary truncate">
                      {member.pid} · {textOf((self ? draft.value : member.value)?.label, lang) || (self ? draft.value : member.value)?.key || "no value set"}
                      {!member.enabled && " · disabled"}
                      {member.enabled && !member.inStock && " · out of stock"}
                    </span>
                  </div>
                  {/* its page in the store, in a new tab; or this product here, in the dialog */}
                  <a
                    href={storeProductUrl(member)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-admin_bordered text-xs font-medium text-admin_primary hover:bg-admin_primary/5 whitespace-nowrap"
                    data-testid="variant-group-member-store"
                  >
                    <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                    View in store
                  </a>
                  {!self && (
                    <button
                      type="button"
                      onClick={() => open(member)}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-admin_bordered text-xs font-medium text-admin_text hover:bg-admin_primary/5 whitespace-nowrap"
                      title="Open this product here, in the dialog"
                      data-testid="variant-group-member-open"
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                      Open
                    </button>
                  )}
                  {/* this product's row keeps the button column */}
                  {self && <span className="w-[78px] shrink-0" />}
                  {!self && !importer && (
                    <Button variant="admin_icon" onClick={() => remove(member)} title="Remove from the group">
                      <XMarkIcon className="w-5 h-5" />
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        </Card>
      )}

      <ProductPicker isOpen={picking} onClose={() => setPicking(false)} onPick={add} exclude={[pid, ...members.map((member) => member.pid)]} />
    </div>
  )
}
